import {
    Avatar,
    Box,
    Button,
    Chip,
    CircularProgress,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import DirectionsRunOutlinedIcon from '@mui/icons-material/DirectionsRunOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import { useTheme } from '@mui/material/styles';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { official } from 'apis';

const sectionSx = {
    border: '1px solid #d9e3ec',
    borderRadius: 2,
    backgroundColor: '#fff',
    overflow: 'hidden'
} as const;

const tableHeaderCellSx = {
    py: 1.25,
    px: 1.5,
    color: '#526173',
    fontWeight: 900,
    fontSize: '0.76rem',
    backgroundColor: '#eef6fb',
    borderBottom: '1px solid #d4e4ef',
    whiteSpace: 'nowrap'
} as const;

const tableBodyCellSx = {
    py: 1.1,
    px: 1.5,
    color: '#0f2338',
    fontSize: '0.9rem',
    fontWeight: 700,
    borderBottom: '1px solid #e1e8ef',
    overflowWrap: 'anywhere'
} as const;

const getInitials = (name?: string) => {
    if (!name) return 'O';
    return name
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0])
        .join('')
        .toUpperCase();
};

const valueOrDash = (value: any) => {
    if (value === null || value === undefined || value === '') return '-';
    return value;
};

const toAccentlessUppercase = (value: string) =>
    value
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toUpperCase();

const formatDate = (value?: string | null) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('el-GR');
};

const getClubPath = (organizationId?: string | null) => (organizationId ? `/public/clubs/manage/${organizationId}/details` : '');

const linkValueSx = {
    m: 0,
    p: 0,
    border: 0,
    background: 'transparent',
    color: '#0f3b68',
    fontFamily: 'inherit',
    fontSize: '0.92rem',
    fontWeight: 900,
    lineHeight: 1.25,
    textAlign: 'left',
    textDecoration: 'none',
    cursor: 'pointer',
    overflowWrap: 'anywhere',
    '&:hover': {
        color: '#1976d2',
        textDecoration: 'underline'
    }
} as const;

const ProfileFact = ({
    label,
    value,
    linkTo,
    href,
    tooltip
}: {
    label: string;
    value: any;
    linkTo?: string;
    href?: string;
    tooltip?: string;
}) => {
    const displayValue = valueOrDash(value);
    const hasRouterLink = Boolean(linkTo && displayValue !== '-');
    const hasHref = Boolean(href && displayValue !== '-');
    const renderedValue = hasRouterLink ? (
        <Typography component={RouterLink} to={linkTo as string} sx={linkValueSx}>
            {displayValue}
        </Typography>
    ) : hasHref ? (
        <Typography component="a" href={href} sx={linkValueSx}>
            {displayValue}
        </Typography>
    ) : (
        <Typography sx={{ color: '#111827', fontSize: '0.92rem', fontWeight: 900, lineHeight: 1.25, overflowWrap: 'anywhere' }}>
            {displayValue}
        </Typography>
    );

    return (
        <Box
            sx={{
                minWidth: 0,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'minmax(120px, 0.75fr) minmax(0, 1fr)' },
                gap: { xs: 0.35, sm: 1.5 },
                alignItems: 'baseline',
                py: 1.05,
                borderBottom: '1px solid rgba(201, 221, 235, 0.72)'
            }}
        >
            <Typography sx={{ color: '#7a8794', fontSize: '0.78rem', fontWeight: 800, lineHeight: 1.15 }}>
                {toAccentlessUppercase(label)}
            </Typography>
            {tooltip && displayValue !== '-' ? (
                <Tooltip title={tooltip} arrow>
                    <Box component="span" sx={{ minWidth: 0, width: 'fit-content', maxWidth: '100%' }}>
                        {renderedValue}
                    </Box>
                </Tooltip>
            ) : (
                renderedValue
            )}
        </Box>
    );
};

const ProfileGroup = ({ title, icon, children }: { title: string; icon: any; children: any }) => (
    <Stack spacing={1.15} sx={{ minWidth: 0, flex: 1, px: { xs: 0, md: 1.25 }, py: { xs: 1, md: 0.5 } }}>
        <Stack direction="row" spacing={1.4} alignItems="center" sx={{ mb: 0.25 }}>
            <Box
                sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#e7f3fc',
                    color: '#4b86b6'
                }}
            >
                {icon}
            </Box>
            <Typography sx={{ color: '#0f3b68', fontSize: { xs: '0.95rem', md: '1.05rem' }, fontWeight: 900, letterSpacing: 0 }}>
                {toAccentlessUppercase(title)}
            </Typography>
        </Stack>
        <Stack sx={{ '& > *:last-child': { borderBottom: 0 } }}>{children}</Stack>
    </Stack>
);

const Details = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const theme = useTheme();

    const profileQuery = useQuery({
        queryKey: ['public-official-profile', id],
        enabled: Boolean(id),
        queryFn: async () => official.getPublicOfficialProfile({ id })
    });

    const data = profileQuery.data;
    const profile = data?.official;
    const functions = data?.functions ?? [];
    const displayName = i18n.language?.startsWith('el') ? profile?.fullName : profile?.fullNameEn || profile?.fullName;
    const profileClubPath = profile?.isOrganizationPublicVisible ? getClubPath(profile?.organizationId) : '';

    if (profileQuery.isLoading) {
        return (
            <Box sx={{ width: '100%', py: 6, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (profileQuery.isError) {
        return (
            <Stack spacing={2} sx={{ width: '100%', py: 3, alignItems: 'center' }}>
                <Typography sx={{ fontWeight: 800 }}>{t('SomethingWrong')}</Typography>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/public/officials')}>
                    {t('BackToList')}
                </Button>
            </Stack>
        );
    }

    if (!profile) {
        return (
            <Stack spacing={2} sx={{ width: '100%', py: 3, alignItems: 'center' }}>
                <Typography sx={{ fontWeight: 800 }}>{t('OfficialNotFound')}</Typography>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/public/officials')}>
                    {t('BackToList')}
                </Button>
            </Stack>
        );
    }

    return (
        <Stack spacing={2.5} sx={{ width: '100%', minWidth: 0 }}>
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/public/officials')}
                sx={{ alignSelf: 'flex-start', color: '#315b9f', fontWeight: 800 }}
            >
                {t('BackToList')}
            </Button>

            <Paper
                elevation={0}
                sx={{
                    ...sectionSx,
                    p: 0,
                    width: '100%',
                    maxWidth: 1260,
                    backgroundColor: '#eef8ff',
                    boxShadow: '0 8px 24px rgba(15, 35, 56, 0.13)'
                }}
            >
                <Stack direction={{ xs: 'column', md: 'row' }} alignItems="stretch" sx={{ minWidth: 0 }}>
                    <Stack
                        direction={{ xs: 'row', md: 'column' }}
                        spacing={{ xs: 1.4, md: 1.6 }}
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                            minWidth: { xs: 0, md: 275 },
                            flex: '0 0 auto',
                            p: { xs: 1.75, md: 2.25 },
                            backgroundColor: '#eaf6ff',
                            borderRight: { xs: 0, md: '1px solid #d4e4ef' },
                            borderBottom: { xs: '1px solid #c9ddeb', md: 0 }
                        }}
                    >
                        <Avatar
                            src={profile.avatar || undefined}
                            sx={{
                                width: { xs: 74, md: 88 },
                                height: { xs: 74, md: 88 },
                                bgcolor: '#7eaaca',
                                color: '#fff',
                                fontWeight: 900,
                                fontSize: '1.4rem',
                                border: '4px solid #f5fbff',
                                boxShadow: '0 6px 14px rgba(49, 91, 159, 0.18)'
                            }}
                        >
                            {getInitials(displayName)}
                        </Avatar>
                        <Box sx={{ minWidth: 0, textAlign: { xs: 'left', md: 'center' } }}>
                            <Typography sx={{ color: '#0f3b68', fontWeight: 900, fontSize: { xs: '1.05rem', md: '1.35rem' }, lineHeight: 1.18 }}>
                                {displayName}
                            </Typography>
                            <Stack direction={{ xs: 'row', md: 'column' }} spacing={0.9} alignItems={{ xs: 'flex-start', md: 'center' }} sx={{ mt: 2 }}>
                                <Chip
                                    icon={<PersonOutlineOutlinedIcon />}
                                    label={`${t('RegNumber')} ${valueOrDash(profile.regNumber)}`}
                                    sx={{
                                        height: 32,
                                        borderRadius: 5,
                                        backgroundColor: '#f7fbff',
                                        border: '1px solid #b8d4ea',
                                        color: '#0f3b68',
                                        fontWeight: 900,
                                        '& .MuiChip-icon': { color: '#4b86b6' }
                                    }}
                                />
                                <Chip
                                    icon={<BadgeOutlinedIcon />}
                                    label={`${t('AthleteCode')} ${valueOrDash(profile.code1 || profile.code2)}`}
                                    sx={{
                                        height: 32,
                                        borderRadius: 5,
                                        backgroundColor: '#f7fbff',
                                        border: '1px solid #b8d4ea',
                                        color: '#0f3b68',
                                        fontWeight: 900,
                                        '& .MuiChip-icon': { color: '#4b86b6' }
                                    }}
                                />
                            </Stack>
                        </Box>
                    </Stack>

                    <Stack
                        direction={{ xs: 'column', lg: 'row' }}
                        spacing={{ xs: 1.5, md: 1.5 }}
                        divider={
                            <Box
                                sx={{
                                    width: { xs: '100%', lg: '1px' },
                                    height: { xs: '1px', lg: 'auto' },
                                    backgroundColor: '#d9e3ec'
                                }}
                            />
                        }
                        sx={{ flex: 1, width: '100%', minWidth: 0, p: { xs: 1.5, md: 2 } }}
                    >
                        <ProfileGroup title={t('BasicInfo')} icon={<AssignmentOutlinedIcon />}>
                            <ProfileFact label={t('LastName')} value={profile.lastName} />
                            <ProfileFact label={t('FirstName')} value={profile.firstName} />
                            <ProfileFact label={t('YearOfBirth')} value={profile.yearOfBirth} />
                            <ProfileFact label={t('Nationality')} value={profile.nationality} />
                        </ProfileGroup>

                        <ProfileGroup title={t('ContactInfo')} icon={<EmailOutlinedIcon />}>
                            <ProfileFact label={t('Email')} value={profile.email} href={profile.email ? `mailto:${profile.email}` : undefined} />
                            <ProfileFact label={t('Mobile')} value={profile.mobile || profile.phone} />
                        </ProfileGroup>

                        <ProfileGroup title={t('AthleticInfo')} icon={<DirectionsRunOutlinedIcon />}>
                            <ProfileFact label={t('Gender')} value={profile.gender} />
                            <ProfileFact
                                label={t('Organization')}
                                value={profile.organizationAbbr || profile.organization}
                                linkTo={profileClubPath}
                                tooltip={profile.organization}
                            />
                            <ProfileFact label={t('Function')} value={profile.function} />
                        </ProfileGroup>
                    </Stack>
                </Stack>
            </Paper>

            <Stack spacing={0.75} sx={{ mt: 1.5, width: '100%', maxWidth: 1260 }}>
                <Stack direction="row" alignItems="center" spacing={1.35}>
                    <Box
                        sx={{
                            width: 42,
                            height: 42,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#e7f3fc',
                            color: '#1976d2',
                            flex: '0 0 auto'
                        }}
                    >
                        <ManageAccountsOutlinedIcon sx={{ fontSize: 22 }} />
                    </Box>
                    <Typography sx={{ color: '#0f3b68', fontWeight: 900, fontSize: { xs: '1.12rem', md: '1.35rem' }, lineHeight: 1.15 }}>
                        {t('Functions')}
                    </Typography>
                    {profileQuery.isFetching && <CircularProgress size={20} />}
                </Stack>

                <TableContainer component={Paper} elevation={0} sx={{ ...sectionSx, width: '100%', overflowX: 'auto' }}>
                    <Table size="small" sx={{ minWidth: 680 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={tableHeaderCellSx}>{t('Function')}</TableCell>
                                <TableCell sx={tableHeaderCellSx}>{t('Organization')}</TableCell>
                                <TableCell sx={tableHeaderCellSx}>{t('DateFrom')}</TableCell>
                                <TableCell sx={tableHeaderCellSx}>{t('DateTo')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {functions.length > 0 ? (
                                functions.map((row: any, index: number) => (
                                    <TableRow
                                        key={row.id}
                                        sx={{
                                            backgroundColor: index % 2 === 0 ? '#fff' : '#fafcfe',
                                            '&:hover': { backgroundColor: '#edf5ff' },
                                            '& .MuiTableCell-root:last-child': { borderBottom: index === functions.length - 1 ? 0 : undefined }
                                        }}
                                    >
                                        <TableCell sx={tableBodyCellSx}>{valueOrDash(row.function)}</TableCell>
                                        <TableCell sx={tableBodyCellSx}>
                                            {row.isOrganizationPublicVisible && row.organizationId && row.organization ? (
                                                <Tooltip title={row.organization} arrow>
                                                    <Typography
                                                        component="button"
                                                        type="button"
                                                        onClick={() => navigate(getClubPath(row.organizationId))}
                                                        sx={linkValueSx}
                                                    >
                                                        {row.organizationAbbr || row.organization}
                                                    </Typography>
                                                </Tooltip>
                                            ) : row.organization ? (
                                                <Tooltip title={row.organization} arrow>
                                                    <Box component="span">{valueOrDash(row.organizationAbbr || row.organization)}</Box>
                                                </Tooltip>
                                            ) : (
                                                valueOrDash(row.organizationAbbr || row.organization)
                                            )}
                                        </TableCell>
                                        <TableCell sx={tableBodyCellSx}>{formatDate(row.dateFrom)}</TableCell>
                                        <TableCell sx={tableBodyCellSx}>{formatDate(row.dateTo)}</TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={4} sx={{ ...tableBodyCellSx, py: 3, textAlign: 'center', color: theme.palette.text.secondary }}>
                                        {t('NoData')}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Stack>
        </Stack>
    );
};

export default Details;
